import { useLocation, useNavigate } from 'react-router-dom';
import {
  Home,
  Wrench,
  Package,
  BarChart3,
  User,
  Zap,
  Shield,
  History,
  UserCircle,
  Layers,
  Clapperboard,
  Sparkles,
  FileText,
} from 'lucide-react';
import { useAppStore } from '@/stores/appStore';
import { useAuthStore } from '@/stores/authStore';
import { cn } from '@/utils/cn';

interface NavItem {
  icon: React.ElementType;
  label: string;
  path: string;
  divider?: false;
}

interface DividerItem {
  divider: true;
  icon?: never;
  label?: never;
  path?: never;
}

type SidebarItem = NavItem | DividerItem;

const baseNavItems: SidebarItem[] = [
  { icon: Home, label: '创作主页', path: '/home' },
  { icon: UserCircle, label: '人像管理', path: '/portraits' },
  { icon: FileText, label: '文案管理', path: '/copywriting' },
  { icon: Sparkles, label: '一站模式', path: '/digital-human' },
  { icon: Clapperboard, label: '剧情模式', path: '/story-drama' },
  { icon: Layers, label: '批量生成', path: '/batch' },
  { icon: History, label: '生成记录', path: '/generate' },
  { icon: Package, label: '资产库', path: '/assets' },
  { icon: Wrench, label: '工具', path: '/tools' },
  { divider: true },
  { icon: BarChart3, label: '用量概览', path: '/usage' },
  { icon: Zap, label: '积分充值', path: '/topup' },
  { icon: User, label: '个人中心', path: '/profile' },
];

const ADMIN_NAV_ITEM: NavItem = { icon: Shield, label: '管理后台', path: '/admin' };

export function Sidebar() {
  const collapsed = useAppStore((s) => s.sidebarCollapsed);
  const toggleSidebar = useAppStore((s) => s.toggleSidebar);
  const location = useLocation();
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);

  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';

  const dividerIndex = baseNavItems.findIndex(
    (item): item is DividerItem => 'divider' in item && item.divider === true
  );
  const topNavItems = baseNavItems.slice(0, dividerIndex) as NavItem[];
  const bottomNavItems = baseNavItems.slice(dividerIndex + 1) as NavItem[];

  if (isAdmin) {
    const profileIndex = bottomNavItems.findIndex((item) => item.path === '/profile');
    const insertAt = profileIndex >= 0 ? profileIndex + 1 : bottomNavItems.length;
    bottomNavItems.splice(insertAt, 0, ADMIN_NAV_ITEM);
  }

  const renderNavItem = (item: NavItem) => {
    const Icon = item.icon;
    const isActive = location.pathname === item.path;

    return (
      <div key={item.path} className="group relative px-2">
        <button
          onClick={() => navigate(item.path)}
          className={cn(
            'w-full flex items-center gap-3 rounded-lg transition-colors duration-150',
            collapsed ? 'justify-center px-2 py-3' : 'px-3 py-2.5',
            isActive
              ? 'bg-surface-hover text-text-primary'
              : 'text-muted hover:bg-surface-hover hover:text-text-primary'
          )}
        >
          <Icon
            size={20}
            className={cn(
              'shrink-0',
              isActive ? 'text-primary' : 'text-muted'
            )}
          />
          {!collapsed && (
            <span className="text-sm font-medium truncate">
              {item.label}
            </span>
          )}
        </button>

        {collapsed && (
          <div className="absolute left-full top-1/2 -translate-y-1/2 ml-2 px-2.5 py-1.5 bg-surface-hover text-text-primary text-xs rounded-md whitespace-nowrap opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-150 border border-border z-50 shadow-lg pointer-events-none">
            {item.label}
            <div className="absolute left-0 top-1/2 -translate-x-1 -translate-y-1/2 w-1.5 h-1.5 bg-surface-hover rotate-45 border-l border-b border-border" />
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      <aside
        className={cn(
          'hidden md:flex flex-col bg-surface border-r border-border transition-all duration-200 ease-out h-[calc(100vh-56px)] sticky top-14',
          collapsed ? 'w-16' : 'w-36'
        )}
      >
        <nav className="flex-1 py-3 space-y-1 overflow-y-auto">
          {topNavItems.map(renderNavItem)}
        </nav>

        <nav className="py-3 space-y-1 border-t border-border">
          {bottomNavItems.map(renderNavItem)}
        </nav>

        <div className="p-2 border-t border-border">
          <button
            onClick={toggleSidebar}
            className={cn(
              'w-full flex items-center justify-center p-2 rounded-lg text-muted hover:bg-surface-hover hover:text-text-primary transition-colors',
              !collapsed && 'gap-2'
            )}
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
              className={cn(
                'transition-transform duration-200',
                collapsed && 'rotate-180'
              )}
            >
              <path
                d="M10 12L6 8L10 4"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            {!collapsed && <span className="text-xs">收起</span>}
          </button>
        </div>
      </aside>

      <nav className="md:hidden fixed bottom-0 left-0 right-0 z-50 bg-surface border-t border-border flex items-center justify-around h-14">
        {[
          { icon: Home, label: '创作', path: '/home' },
          { icon: FileText, label: '文案', path: '/copywriting' },
          { icon: History, label: '记录', path: '/generate' },
          { icon: Package, label: '资产库', path: '/assets' },
          ...(isAdmin ? [{ icon: Shield, label: '后台', path: '/admin' }] : []),
          { icon: User, label: '个人', path: '/profile' },
        ].map((item) => {
          const Icon = item.icon;
          const isActive = location.pathname === item.path;
          return (
            <button
              key={item.path}
              onClick={() => navigate(item.path)}
              className={cn(
                'flex flex-col items-center justify-center gap-0.5 flex-1 h-full transition-colors',
                isActive ? 'text-primary' : 'text-muted'
              )}
            >
              <Icon size={20} />
              <span className="text-[10px]">{item.label}</span>
            </button>
          );
        })}
      </nav>
    </>
  );
}
